import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.ts';
import { EMPTY_FILTERS, Filters, type FilterState } from './components/Filters.tsx';
import { RaceCard } from './components/RaceCard.tsx';
import { RaceDetail } from './components/RaceDetail.tsx';
import { SourcesPanel } from './components/SourcesPanel.tsx';
import { VerifyBar } from './components/VerifyBar.tsx';
import { useI18n } from './i18n.tsx';
import { useTheme } from './useTheme.ts';
import type { RaceWithVerification } from './types.ts';

function matches(race: RaceWithVerification, f: FilterState): boolean {
  if (f.month && Number(race.startDate.slice(5, 7)) !== Number(f.month)) return false;
  if (f.county && race.county !== f.county) return false;
  if (f.terrain && race.terrain !== f.terrain) return false;
  if (f.status && (race.verification?.status ?? 'unverified') !== f.status) return false;
  if (f.maxKm && race.half.km > Number(f.maxKm)) return false;
  if (f.q.trim()) {
    const needle = f.q.trim().toLowerCase();
    const haystack = [race.name, race.location, race.county, race.area].join(' ').toLowerCase();
    if (!haystack.includes(needle)) return false;
  }
  return true;
}

export function App() {
  const { t, lang, setLang } = useI18n();
  const { theme, toggleTheme } = useTheme();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [selected, setSelected] = useState<RaceWithVerification | null>(null);

  const racesQuery = useQuery({ queryKey: ['races'], queryFn: api.races });
  const metaQuery = useQuery({ queryKey: ['meta'], queryFn: api.meta });
  const sourcesQuery = useQuery({ queryKey: ['sources'], queryFn: api.sources });
  const verificationQuery = useQuery({
    queryKey: ['verification'],
    queryFn: api.verification,
    // Poll while a pass is in flight so the progress line stays live.
    refetchInterval: (query) => (query.state.data?.progress.running ? 1200 : false),
  });

  const running = verificationQuery.data?.progress.running ?? false;
  const lastRunAt = verificationQuery.data?.lastRun?.runAt ?? null;

  // When a run finishes, pull the fresh statuses in.
  const seenRunAt = useRef<string | null>(null);
  useEffect(() => {
    if (running || !lastRunAt || lastRunAt === seenRunAt.current) return;
    seenRunAt.current = lastRunAt;
    for (const key of ['races', 'meta', 'sources']) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  }, [running, lastRunAt, queryClient]);

  const verify = useMutation({
    mutationFn: api.startVerification,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['verification'] }),
  });

  const races = racesQuery.data?.races ?? [];

  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const race of races) {
      const status = race.verification?.status ?? 'unverified';
      out[status] = (out[status] ?? 0) + 1;
    }
    return out;
  }, [races]);

  const visible = useMemo(() => races.filter((r) => matches(r, filters)), [races, filters]);

  const byMonth = useMemo(() => {
    const groups = new Map<number, RaceWithVerification[]>();
    for (const race of visible) {
      const month = Number(race.startDate.slice(5, 7));
      const list = groups.get(month) ?? [];
      list.push(race);
      groups.set(month, list);
    }
    return [...groups.entries()].sort((a, b) => a[0] - b[0]);
  }, [visible]);

  return (
    <>
      <header className="site-header">
        <div className="header-inner">
          <div className="brand">
            <h1>
              {t.title} <span className="year">2026</span>
            </h1>
            <p>{t.subtitle}</p>
          </div>

          <div className="header-tools">
            <div className="segmented" role="group" aria-label="Language">
              {(['ro', 'en'] as const).map((code) => (
                <button
                  key={code}
                  type="button"
                  aria-pressed={lang === code}
                  onClick={() => setLang(code)}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={toggleTheme}
              aria-label={t.theme}
              title={t.theme}
            >
              {theme === 'dark' ? '☀' : '☾'}
            </button>
          </div>
        </div>
      </header>

      <main className="shell">
        <VerifyBar
          state={verificationQuery.data}
          counts={counts}
          activeStatus={filters.status}
          onToggleStatus={(status) =>
            setFilters((f) => ({ ...f, status: f.status === status ? '' : status }))
          }
          onVerify={() => verify.mutate()}
        />

        <Filters
          value={filters}
          meta={metaQuery.data}
          onChange={setFilters}
          onReset={() => setFilters(EMPTY_FILTERS)}
        />

        {racesQuery.isPending ? (
          <div className="race-grid">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="skeleton" />
            ))}
          </div>
        ) : racesQuery.isError ? (
          <p className="state">{String(racesQuery.error)}</p>
        ) : visible.length === 0 ? (
          <p className="state">{t.noResults}</p>
        ) : (
          <>
            <p className="result-count">
              {visible.length} {visible.length === 1 ? t.raceOne : t.races}
              {visible.length !== races.length ? ` / ${races.length}` : ''}
            </p>

            {byMonth.map(([month, list]) => (
              <section className="month-block" key={month}>
                <div className="month-head">
                  <h2>{t.monthNames[month - 1]}</h2>
                  <span className="rule" />
                  <span className="n">{list.length}</span>
                </div>
                <div className="race-grid">
                  {list.map((race) => (
                    <RaceCard key={race.id} race={race} onOpen={setSelected} />
                  ))}
                </div>
              </section>
            ))}
          </>
        )}

        <SourcesPanel sources={sourcesQuery.data} />
      </main>

      <RaceDetail
        race={selected}
        sources={metaQuery.data?.sources ?? []}
        onClose={() => setSelected(null)}
      />
    </>
  );
}
